// Prédiction de désabonnement — clients à risque identifiés par l'IA
import { useState, useMemo } from "react";
import { Users, MessageCircle, Phone } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { useChurnPrediction } from "@/hooks/admin/useAI";
import type { ChurnRisk, ChurnRiskLevel } from "@/lib/admin-api/ai";
import React from "react";

// ---------------------------------------------------------------------------
// Risk level helpers
// ---------------------------------------------------------------------------

const RISK_BADGE: Record<ChurnRiskLevel, string> = {
  critical: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-green-100 text-green-700",
};

const RISK_LABELS: Record<ChurnRiskLevel, string> = {
  critical: "Critique",
  high: "Élevé",
  medium: "Modéré",
  low: "Faible",
};

const RISK_LEVELS: readonly { value: ChurnRiskLevel; label: string }[] = [
  { value: "critical", label: "Critique" },
  { value: "high", label: "Élevé" },
  { value: "medium", label: "Modéré" },
  { value: "low", label: "Faible" },
];

// ---------------------------------------------------------------------------
// Progress bar for churn probability
// ---------------------------------------------------------------------------

function ChurnBar({ pct }: { readonly pct: number }) {
  const color =
    pct >= 80
      ? "bg-red-500"
      : pct >= 60
        ? "bg-orange-400"
        : pct >= 40
          ? "bg-amber-400"
          : "bg-green-400";
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-stone-200">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-stone-700">{pct}%</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact modal
// ---------------------------------------------------------------------------

interface ContactModalProps {
  readonly customer: ChurnRisk | null;
  readonly onClose: () => void;
}

function ContactModal({ customer, onClose }: ContactModalProps) {
  const whatsappUrl = customer
    ? `https://wa.me/${customer.phone.replace(/\D/g, "")}`
    : "#";

  return (
    <AdminModal
      title={customer ? `Contacter — ${customer.customer_name}` : "Contacter"}
      open={Boolean(customer)}
      onClose={onClose}
    >
      {customer && (
        <div className="space-y-4">
          <div className="rounded-lg bg-stone-50 p-4 text-sm">
            <p className="text-stone-500">
              Téléphone : <span className="font-medium text-stone-800">{customer.phone}</span>
            </p>
            <p className="mt-1 text-stone-500">
              Dernière commande :{" "}
              <span className="font-medium text-stone-800">
                il y a {customer.days_since_order} jour
                {customer.days_since_order > 1 ? "s" : ""}
              </span>
            </p>
            <p className="mt-1 text-stone-500">
              Action recommandée :{" "}
              <span className="font-medium text-stone-800">{customer.recommended_action}</span>
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
            <a
              href={`tel:${customer.phone}`}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
            >
              <Phone className="h-4 w-4" />
              Appeler
            </a>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-stone-400 hover:text-stone-600"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function ChurnPrediction() {
  const [riskLevel, setRiskLevel] = useState<ChurnRiskLevel | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [contactCustomer, setContactCustomer] = useState<ChurnRisk | null>(null);

  const params = useMemo(
    () => ({
      risk_level: (riskLevel || null) as ChurnRiskLevel | null,
      date_from: dateFrom || null,
      date_to: dateTo || null,
    }),
    [riskLevel, dateFrom, dateTo],
  );

  const { data, isLoading, isError } = useChurnPrediction(params);

  const customers = data?.customers ?? [];
  const criticalCount = customers.filter((c) => c.risk_level === "critical").length;
  const highCount = customers.filter((c) => c.risk_level === "high").length;

  const columns: readonly ColumnDef<ChurnRisk>[] = useMemo(
    () => [
      {
        id: "customer_name",
        header: "Client",
        cell: (row) => (
          <span className="text-sm font-medium text-stone-900">{row.customer_name}</span>
        ),
      },
      {
        id: "phone",
        header: "Téléphone",
        cell: (row) => <span className="text-sm text-stone-500">{row.phone}</span>,
      },
      {
        id: "last_order_at",
        header: "Dernière commande",
        cell: (row) => (
          <div>
            <p className="text-sm text-stone-700">
              {new Date(row.last_order_at).toLocaleDateString("fr-SN")}
            </p>
            <p className="text-xs text-stone-400">il y a {row.days_since_order}j</p>
          </div>
        ),
      },
      {
        id: "churn_probability_percent",
        header: "Risque désabo.",
        cell: (row) => <ChurnBar pct={row.churn_probability_percent} />,
      },
      {
        id: "risk_level",
        header: "Niveau",
        cell: (row) => (
          <span
            className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${RISK_BADGE[row.risk_level]}`}
          >
            {RISK_LABELS[row.risk_level]}
          </span>
        ),
      },
      {
        id: "recommended_action",
        header: "Action recommandée",
        cell: (row) => <span className="text-xs text-stone-500">{row.recommended_action}</span>,
      },
      {
        id: "actions",
        header: "",
        cell: (row) => (
          <button
            type="button"
            onClick={() => setContactCustomer(row)}
            className="inline-flex items-center gap-1 rounded-md bg-stone-100 px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-200"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            Contacter
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Prédiction de désabonnement</h1>
          <p className="mt-1 text-sm text-stone-500">
            Clients identifiés par l'IA comme susceptibles de ne plus commander
          </p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KPICard
            label="Clients critiques"
            value={isLoading ? "…" : String(criticalCount)}
            icon={<Users className="h-4 w-4" />}
          />
          <KPICard
            label="Risque élevé"
            value={isLoading ? "…" : String(highCount)}
            icon={<Users className="h-4 w-4" />}
          />
          <KPICard
            label="Risque moyen global"
            value={isLoading ? "…" : `${data?.avg_churn_risk_percent ?? 0}%`}
            icon={<Users className="h-4 w-4" />}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={riskLevel}
            onChange={(e) => setRiskLevel(e.target.value as ChurnRiskLevel | "")}
            className="rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
          >
            <option value="">Tous les niveaux</option>
            {RISK_LEVELS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <label className="text-sm text-stone-500">Dernière commande du</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
            />
            <span className="text-sm text-stone-400">au</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Table */}
        {isError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Impossible de charger les prédictions. Veuillez réessayer.
          </div>
        ) : (
          <AdminDataTable
            data={customers}
            columns={columns}
            isLoading={isLoading}
            emptyMessage="Aucun client à risque détecté."
            getRowId={(row) => row.customer_id}
          />
        )}
      </div>

      <ContactModal customer={contactCustomer} onClose={() => setContactCustomer(null)} />
    </AdminLayout>
  );
}
